import { useCallback, useEffect } from 'react';
import { parseAddressFromGeocoder } from '@/shared/lib';
import { ClientLocationDto } from '@/app/store/api/restApi';
import { useLazyGetGeocodeQuery, useGetOptimalDeliveryZoneMutation } from '@/entities';
import { useChangePersonalInfoDelivery } from '../orderPersonalInfo';
import { useAppSelector, useAppDispatch, setOrderInfoLoading } from '@/app/store';

export const useActualiseOptimalDeliveryZoneOnLocations = ({
  locations,
  locationsLoading,
}: {
  locations: ClientLocationDto[];
  locationsLoading: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { fulfillmentToTime } = useAppSelector((state) => state.order);

  const { delivery, onSetDeliveryZone, onSetNationwideInfo, onClearDeliveryZone, onClearDeliveryAddress } =
    useChangePersonalInfoDelivery(false);

  const [getOptimalDeliveryZone] = useGetOptimalDeliveryZoneMutation();
  const [triggerGeocode] = useLazyGetGeocodeQuery();

  const actualiseOptimalDZ = useCallback(async () => {
    if (!delivery.coords) return;

    dispatch(setOrderInfoLoading(true));
    try {
      const zone = await getOptimalDeliveryZone({
        getOptimalDeliveryZoneDto: {
          orderPrice: 0,
          point: delivery.coords,
          date: fulfillmentToTime,
          timeSlotsAdjustmentInfo: undefined,
        },
      }).unwrap();

      if (!zone.data) return;

      if (zone.data.nationwideInfo) {
        const nwLocation = zone.data.nationwideInfo.location;
        const nwDeliveryPrice = zone.data.nationwideInfo.deliveryPrice;
        const geocodeData = await triggerGeocode({
          geocode: `${nwLocation.coordinates.lng},${nwLocation.coordinates.lat}`,
        }).unwrap();
        const geocodeObject = geocodeData.response.GeoObjectCollection.featureMember[0].GeoObject;
        const { city } = parseAddressFromGeocoder(geocodeObject);

        onSetDeliveryZone({
          price: undefined,
          zoneId: undefined,
          locationId: undefined,
          paymentTypes: nwLocation.delivery?.paymentTypes ?? [],
          onlinePayment: nwLocation.delivery?.onlinePayment ?? undefined,
        });

        onSetNationwideInfo({ locationId: nwLocation.id, city, price: nwDeliveryPrice });
      } else {
        onSetNationwideInfo(undefined);

        if (zone.data.deliveryZone) {
          onSetDeliveryZone({
            zoneId: zone.data.deliveryZone.deliveryZone.id,
            locationId: zone.data.deliveryZone.location.id,
            price: zone.data.deliveryZone.deliveryPrice ?? 0,
            paymentTypes: zone.data.deliveryZone.availablePaymentTypes,
            onlinePayment: zone.data.deliveryZone.location.delivery?.onlinePayment ?? undefined,
          });
        } else {
          onClearDeliveryZone();
        }
      }
    } catch {
      onClearDeliveryZone();
      onClearDeliveryAddress();
      onSetNationwideInfo(undefined);
    } finally {
      dispatch(setOrderInfoLoading(false));
    }
  }, [
    delivery.coords,
    dispatch,
    fulfillmentToTime,
    getOptimalDeliveryZone,
    onClearDeliveryAddress,
    onClearDeliveryZone,
    onSetDeliveryZone,
    onSetNationwideInfo,
    triggerGeocode,
  ]);

  useEffect(() => {
    if (locationsLoading) return;
    if (locations.length === 0) return;
    const deliveryAvailable = locations.find((loc) => loc.delivery?.isActive);
    if (!deliveryAvailable) return;

    actualiseOptimalDZ();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, locationsLoading]);
};
